// src/pages/LiqB.jsx   (or name it LiquidityExpandedGuide.jsx, etc.)
import React from 'react';

const pageStyles = `
  body {
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    line-height: 1.6;
    color: #333;
    max-width: 900px;
    margin: 0 auto;
    padding: 40px 20px;
    background-color: #f9f9f9;
  }
  header {
    text-align: center;
    margin-bottom: 40px;
  }
  h1, h2, h3 {
    color: #1a3c6d;
  }
  h1 {
    border-bottom: 2px solid #1a3c6d;
    padding-bottom: 10px;
  }
  .section {
    background: white;
    padding: 25px;
    margin-bottom: 30px;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.08);
  }
  .image-container {
    margin: 25px 0;
    text-align: center;
  }
  .image-container img {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 0 auto 10px;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  }
  .image-container figcaption {
    font-style: italic;
    color: #555;
    font-size: 0.95em;
  }
  .highlight {
    background-color: #fff3cd;
    padding: 15px;
    border-left: 5px solid #ffc107;
    margin: 20px 0;
  }
  footer {
    text-align: center;
    margin-top: 50px;
    color: #666;
    font-size: 0.9em;
  }
`;

export default function LiqB() {
  return (
    <>
      <style>{pageStyles}</style>

      <header>
        <h1>Understanding Liquidity & Monthly Sell Limits in the MEV Project</h1>
        <p>Expanded Guide for Patrons – How Liquidity Enables Safe Selling</p>
      </header>

      <div className="section">
        <p>Dear MEV Patrons,</p>
        <p>
          Building on our previous guide, we're diving deeper into how <strong>liquidity</strong> directly determines how much MEV can be sold safely—especially on a monthly basis—without causing sharp price drops. Since MEV is traded exclusively on our platform (for BNB), the liquidity pool is the only marketplace, making these dynamics even more important.
        </p>
      </div>

      <div className="section">
        <h2>Core Concept: Liquidity Determines Safe Sell Volumes</h2>
        <p>
          In our AMM-style pool, the price is set by the balance of MEV and BNB. Selling MEV adds tokens to the pool and removes BNB, shifting the ratio and causing slippage (price drop). Higher liquidity means flatter price impact curves—allowing larger sells with less disruption.
        </p>
        <p>
          Your monthly sell limits per patron are tied to this: they spread out sales to protect the pool. As we grow liquidity (e.g., more BNB added through project earnings), these limits can increase, giving you more freedom to sell.
        </p>

        <div className="image-container">
          <img
            src="https://images.ctfassets.net/oc3ca6rftwdu/5Su2EgjbYa5oyr274KWxMI/c1bbc67c8ba2053769e8546459e30fa0/Graph__1_.jpg"
            alt="AMM Liquidity Pool Diagram"
          />
          <figcaption>Basic AMM Liquidity Pool – Balanced MEV/BNB sides with constant product curve (Uniswap-style)</figcaption>
        </div>

        <p>
          This diagram shows how the pool maintains balance. Selling shifts the curve, but larger pools absorb bigger shifts better.
        </p>
      </div>

      <div className="section">
        <h2>Slippage Curves: How Liquidity Affects Sell Amounts</h2>
        <p>
          These curves show price impact (%) vs. trade size (amount of MEV sold). Low liquidity = steep curve (big drop even on small sells). High liquidity = flat curve (sell more with minimal impact).
        </p>
        <p>
          Example: With current ~25 BNB liquidity, selling 100–200 MEV might keep slippage low (1–5%). Selling 1,000 MEV could cause 20%+ drop. Growing liquidity lets us raise monthly caps safely.
        </p>

        <div className="image-container">
          <img
            src="https://cdn.sanity.io/images/dgybcd83/production/77445c2f27457e58406bc250c491481273cdccdb-1002x615.png"
            alt="Slippage Curve Graph – Price Impact vs Trade Size"
          />
          <figcaption>Paradigm's classic slippage graph: Multiple curves for different liquidity levels (low = steep, high = flat)</figcaption>
        </div>

        <div className="image-container">
          <img
            src="https://cdn.sanity.io/images/fegzy4pk/production/54c55c6401cbc4521487cbe82d1cde1c7c98494b-1536x1024.png?rect=0,109,1536,806&w=1200&h=630&sat=-100"
            alt="Slippage Impact Chart"
          />
          <figcaption>Another view: Slippage % rises sharply beyond a certain trade size, depending on pool depth</figcaption>
        </div>
      </div>

      <div className="section">
        <h2>Liquidity vs. Maximum Sellable Volume (Monthly Perspective)</h2>
        <p>
          As liquidity grows, the "safe sell zone" expands. This directly allows higher monthly sell allowances community-wide and per patron—without risking stability.
        </p>
        <p>
          For example: At current liquidity, total monthly sells might be capped at ~1,000–2,000 MEV across all patrons. Doubling liquidity could support 5,000+ MEV/month safely.
        </p>

        <div className="image-container">
          <img
            src="https://miro.medium.com/v2/resize:fit:1400/1*9mFqQciafgSIiEkUN7sZtA.png"
            alt="Liquidity Depth and Trade Capacity"
          />
          <figcaption>Liquidity pool size vs. allowable trade volume – deeper pools support larger sells</figcaption>
        </div>

        <div className="image-container">
          <img
            src="https://www.mdpi.com/risks/risks-12-00003/article_deploy/html/images/risks-12-00003-g001.png"
            alt="DeFi Liquidity Simulation Graph"
          />
          <figcaption>Simulation showing dynamic performance: Higher liquidity reduces volatility for larger volumes</figcaption>
        </div>
      </div>

      <div className="section">
        <h2>Why This Matters for You</h2>
        <p className="highlight">
          Monthly limits aren't arbitrary—they're calibrated to current liquidity to prevent price crashes. As we grow the pool together (through platform utilities, reinvestments, etc.), limits can relax, increasing your realistic ability to sell MEV at stable prices.
        </p>
        <p>
          We're committed to transparency and gradual, sustainable growth. Your holdings gain real value as liquidity deepens.
        </p>
      </div>

      <div className="section">
        <h2>Looking Ahead</h2>
        <p>
          We're actively increasing liquidity to give everyone more flexibility. Hold, use, or sell gradually—together we keep MEV strong and valuable for the long term.
        </p>
        <p>Questions? Reach out—we're in this together!</p>
      </div>

      <footer>
        <p>
          Best regards,<br />
          The MEV Team
        </p>
        <p>January 2026</p>
      </footer>
    </>
  );
}